import React, { useState } from "react";
import FilesListV2 from "../../../../../../components/molecules/FilesListV2";
import { useSingleSpendingDemand } from "../../../../hooks";
import { getFileUrlFromId } from "../../../../../../utils/fileReader";
import useUserAuth from "../../../../../auth/hook/useUserAuth";
import { userRolesCode } from "../../../../../auth/types/user.types";
import InputFile from "../../../../../../components/atoms/InputFile/InputFile";
import DeleteFilePasswordModal from "../../../../../../components/molecules/DeleteFilePasswordModal/DeleteFilePasswordModal";

export interface FileListSpendingDemandProps {
  spendingDemandId?: string;
}

// Same accepted types as FileListManager (NewSpendingDemandForm.tsx)
const ACCEPTED_FILE_TYPES = "image/*,.pdf,.xls,.xlsx,.xlsm,.xlsb,.csv";

const ADMIN_ROLES: number[] = [
  userRolesCode.ADMIN,
  userRolesCode.SUPER_ADMIN,
  userRolesCode.ADMIN_PLUS,
];

export default function FilesList({
  spendingDemandId,
}: FileListSpendingDemandProps) {
  const {
    getListFilesOfSpendingDemand,
    deleteFileOfSpendingDemand,
    addFileOfSpendingDemand,
  } = useSingleSpendingDemand(spendingDemandId);
  const { userInfo } = useUserAuth();

  const isAdmin =
    userInfo?.user_role !== undefined && ADMIN_ROLES.includes(userInfo.user_role);

  const [fileIdPendingDelete, setFileIdPendingDelete] = useState<string | null>(null);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  if (getListFilesOfSpendingDemand.isError) {
    return <div>Error...</div>;
  }

  const handleAddFiles = (newFiles: File[]) => {
    newFiles.forEach((file) => {
      addFileOfSpendingDemand.run({ fileURL: file });
    });
  };

  const handleCloseDeleteModal = () => {
    setFileIdPendingDelete(null);
    setDeleteError(null);
  };

  const handleConfirmDelete = (password: string) => {
    if (!fileIdPendingDelete) return;
    setDeleteError(null);
    deleteFileOfSpendingDemand.run(
      { spendingDemandFileId: fileIdPendingDelete, password },
      {
        onSuccess: () => {
          setFileIdPendingDelete(null);
        },
        onError: (error: any) => {
          const status = error?.response?.status;
          setDeleteError(
            status === 401 || status === 403
              ? "Mot de passe incorrect ou accès refusé."
              : "Une erreur est survenue, veuillez réessayer."
          );
        },
      }
    );
  };

  const files = getListFilesOfSpendingDemand.data?.data?.map((file) => ({
    id: file.spdf_file_id,
    src: getFileUrlFromId(file.spdf_file_id),
    name: file.spdf_file_name,
    type: file.spdf_file_type,
    onDelete: isAdmin
      ? () => {
          setDeleteError(null);
          setFileIdPendingDelete(file.spdf_id);
        }
      : undefined,
  }));

  return (
    <>
      <FilesListV2
        files={files}
        isLoading={
          getListFilesOfSpendingDemand.isPending ||
          deleteFileOfSpendingDemand.isPending ||
          addFileOfSpendingDemand.isPending
        }
      />
      <div style={{ marginTop: "12px" }}>
        <InputFile
          addFiles={handleAddFiles}
          label="Ajouter"
          accept={ACCEPTED_FILE_TYPES}
        />
      </div>
      {fileIdPendingDelete && (
        <DeleteFilePasswordModal
          onClose={handleCloseDeleteModal}
          onConfirm={handleConfirmDelete}
          isLoading={deleteFileOfSpendingDemand.isPending}
          error={deleteError}
        />
      )}
    </>
  );
}
